import React, {useState, useEffect} from "react";
import {FormattedMessage} from "react-intl";
import Box from "@amzn/meridian/box";
import Row from "@amzn/meridian/row";
import Column from "@amzn/meridian/column";
import Text from "@amzn/meridian/text";
import Tag from "@amzn/meridian/tag";
import Divider from "@amzn/meridian/divider";
import Button from "@amzn/meridian/button";
import Icon from "@amzn/meridian/icon";
import chevronDownTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import chevronRightTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import get from "lodash/get";
import Loading from "../util/Loading";

/**
 * Renders the unified active + scheduled path schedule list for the selected cycle.
 *
 * The active entry (if any) is always rendered first with a visual distinction.
 * Scheduled (future) entries follow in effective-date order. Every row can be
 * expanded to reveal its default path configuration, day-of-week overrides, and
 * audit metadata. Only scheduled rows expose a Delete affordance — the active entry
 * is protected (matching the DeletePathSchedule backend behavior).
 */
const STATUS = {
    ACTIVE: "ACTIVE",
    SCHEDULED: "SCHEDULED"
};

const PATH_SEPARATOR = " / ";
const KEY_SEPARATOR = "#";

const PathScheduleList = (props) => {
    const {isLoading, error, activeSchedule, scheduled, onDeleteScheduled} = props;

    const [expandedRowKey, setExpandedRowKey] = useState(undefined);

    // Reset row expansion whenever the underlying schedule set changes (e.g. the user
    // switches cycle or date range). Row keys are STATUS#effectiveDate#index, so a row in
    // a newly loaded set could otherwise collide with the previously expanded key and
    // render pre-expanded.
    useEffect(() => {
        setExpandedRowKey(undefined);
    }, [activeSchedule, scheduled]);

    const rowKey = (status, entry, index) =>
        status + KEY_SEPARATOR + get(entry, ["effectiveDate"], "") + KEY_SEPARATOR + index;

    const toggleRow = (key) => setExpandedRowKey(expandedRowKey === key ? undefined : key);

    const formatDate = (value) => {
        if (!value) {
            return "-";
        }
        return value;
    };

    const formatTimestamp = (millis) => {
        if (!millis || isNaN(new Date(Number(millis)).getTime())) {
            return "-";
        }
        return new Date(Number(millis)).toLocaleString();
    };

    const renderConfigurationSummary = (entry) => {
        const fillPathId = get(entry, ["defaultPathConfig", "fillPathId"]);
        const pickPathId = get(entry, ["defaultPathConfig", "pickPathId"]);
        if (!fillPathId && !pickPathId) {
            return "-";
        }
        return (fillPathId || "-") + PATH_SEPARATOR + (pickPathId || "-");
    };

    const renderOverrides = (entry) => {
        const overrides = get(entry, ["dayOfWeekOverrides"]);
        if (!overrides || Object.keys(overrides).length === 0) {
            return (
                <Text type="b300" color="secondary">
                    <FormattedMessage id="pathSchedule.noOverrides" defaultMessage="None"/>
                </Text>
            );
        }
        return (
            <Column spacing="xxsmall">
                {Object.keys(overrides).map((day) => (
                    <Text key={day} type="b300">
                        {day + ": " + get(overrides, [day, "fillPathId"], "-") + PATH_SEPARATOR
                            + get(overrides, [day, "pickPathId"], "-")}
                    </Text>
                ))}
            </Column>
        );
    };

    const renderDetailField = (labelId, defaultLabel, valueNode) => (
        <Row spacingInset={"none none xsmall none"} alignmentVertical={"top"}>
            <Column width={"30%"}>
                <Text type="b300" color="secondary">
                    <FormattedMessage id={labelId} defaultMessage={defaultLabel}/>
                </Text>
            </Column>
            <Column width={"70%"}>
                {typeof valueNode === "string"
                    ? <Text type="b300">{valueNode}</Text>
                    : valueNode}
            </Column>
        </Row>
    );

    const renderExpandedDetail = (status, entry) => (
        <Box type="outline" spacingInset={"medium"}>
            <Column spacing={"xsmall"}>
                {renderDetailField("pathSchedule.detail.fillPath", "Fill Path",
                    get(entry, ["defaultPathConfig", "fillPathId"], "-"))}
                {renderDetailField("pathSchedule.detail.pickPath", "Pick Path",
                    get(entry, ["defaultPathConfig", "pickPathId"], "-"))}
                {renderDetailField("pathSchedule.detail.overrides", "Day-of-Week Overrides",
                    renderOverrides(entry))}
                {renderDetailField("pathSchedule.detail.effectiveDate", "Effective Date",
                    formatDate(get(entry, ["effectiveDate"])))}
                {renderDetailField("pathSchedule.detail.lastUpdated", "Last Updated",
                    formatTimestamp(get(entry, ["lastUpdated"])))}
                {renderDetailField("pathSchedule.detail.requester", "Updated By",
                    get(entry, ["requester"], "-"))}
                {status === STATUS.SCHEDULED && typeof onDeleteScheduled === "function" &&
                    <Row alignmentHorizontal={"right"} spacingInset={"small none none none"}>
                        <Button type="secondary" size="small" onClick={() => onDeleteScheduled(entry)}>
                            <FormattedMessage id="pathSchedule.action.delete" defaultMessage="Delete"/>
                        </Button>
                    </Row>
                }
            </Column>
        </Box>
    );

    const renderRow = (status, entry, index) => {
        const key = rowKey(status, entry, index);
        const isExpanded = expandedRowKey === key;
        const isActive = status === STATUS.ACTIVE;

        return (
            <Column key={key} spacing={"none"} width={"100%"}>
                <Box type={isActive ? "fill" : "outline"} spacingInset={"small medium"}>
                    <Row width={"100%"} alignmentVertical={"center"}>
                        <Column width={"15%"}>
                            <Tag type={isActive ? "success" : "theme"}>
                                {isActive
                                    ? <FormattedMessage id="pathSchedule.status.active" defaultMessage="ACTIVE"/>
                                    : <FormattedMessage id="pathSchedule.status.scheduled" defaultMessage="SCHEDULED"/>}
                            </Tag>
                        </Column>
                        <Column width={"25%"}>
                            <Text type={isActive ? "b400" : "b300"}>{formatDate(get(entry, ["effectiveDate"]))}</Text>
                        </Column>
                        <Column width={"40%"}>
                            <Text type="b300">{renderConfigurationSummary(entry)}</Text>
                        </Column>
                        <Column width={"20%"} alignmentHorizontal={"right"}>
                            <Button type="icon" size="small" onClick={() => toggleRow(key)}>
                                <Icon tokens={isExpanded ? chevronDownTokens : chevronRightTokens}>
                                    {isExpanded ? "Collapse" : "Expand"}
                                </Icon>
                            </Button>
                        </Column>
                    </Row>
                </Box>
                {isExpanded && renderExpandedDetail(status, entry)}
            </Column>
        );
    };

    const renderHeader = () => (
        <Row spacingInset={"xsmall medium"}>
            <Column width={"15%"}>
                <Text type="h300">
                    <FormattedMessage id="pathSchedule.header.status" defaultMessage="Status"/>
                </Text>
            </Column>
            <Column width={"25%"}>
                <Text type="h300">
                    <FormattedMessage id="pathSchedule.header.effectiveDate" defaultMessage="Effective Date"/>
                </Text>
            </Column>
            <Column width={"40%"}>
                <Text type="h300">
                    <FormattedMessage id="pathSchedule.header.configuration" defaultMessage="Configuration (Fill / Pick)"/>
                </Text>
            </Column>
            <Column width={"20%"} alignmentHorizontal={"right"}>
                <Text type="h300">
                    <FormattedMessage id="pathSchedule.header.actions" defaultMessage="Actions"/>
                </Text>
            </Column>
        </Row>
    );

    const renderBody = () => {
        if (isLoading) {
            return <Loading height={"120px"}/>;
        }
        if (error) {
            return (
                <Row alignmentHorizontal={"center"} spacingInset={"medium"}>
                    <Text type="b300" color="error">{get(error, ["message"]) || String(error)}</Text>
                </Row>
            );
        }
        const hasActive = !!activeSchedule && !!get(activeSchedule, ["effectiveDate"]);
        // Sort by effectiveDate so rendered order matches the documented contract,
        // regardless of backend ordering. .slice() avoids mutating the prop.
        const sortedScheduled = (Array.isArray(scheduled) ? scheduled : [])
            .slice()
            .sort((a, b) =>
                get(a, ["effectiveDate"], "").localeCompare(get(b, ["effectiveDate"], "")));
        if (!hasActive && sortedScheduled.length === 0) {
            return (
                <Row alignmentHorizontal={"center"} spacingInset={"medium"}>
                    <Text type="b300" color="secondary">
                        <FormattedMessage id="pathSchedule.empty"
                                          defaultMessage="No active or scheduled schedules for the selected cycle."/>
                    </Text>
                </Row>
            );
        }
        return (
            <Column spacing={"xsmall"} width={"100%"}>
                {hasActive && renderRow(STATUS.ACTIVE, activeSchedule, 0)}
                {sortedScheduled.map((entry, index) => renderRow(STATUS.SCHEDULED, entry, index))}
            </Column>
        );
    };

    return (
        <Column width={"100%"} spacing={"none"} overflowX={"auto"} minWidth={"fit-content"}>
            {renderHeader()}
            <Divider/>
            <Column spacingInset={"small none"} width={"100%"}>
                {renderBody()}
            </Column>
        </Column>
    );
};

export default PathScheduleList;
